import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Link } from "react-router-dom";
import { Dropdown, DropdownToggle, DropdownMenu, Row, Col, Badge } from "reactstrap";
import SimpleBar from "simplebar-react";
import { getTicketNotifications, markTicketNotificationAsRead } from "../../Api/GmsTicket";

//i18n
import { withTranslation } from "react-i18next";

const MaintenanceNotificationDropdown = props => {
  const [menu, setMenu] = useState(false);
  const [ticketNotifications, setTicketNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadNotifications();
    // Refresh every 2 minutes
    const interval = setInterval(loadNotifications, 120000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = authUser.userId || authUser.user_Id || null;
      const employeeId = authUser.employeeId || user.user_EmployeeId || null;
      
      let allTickets = [];
      if (userId) {
        try {
          const userNotifs = await getTicketNotifications(userId);
          if (Array.isArray(userNotifs)) allTickets = [...allTickets, ...userNotifs];
        } catch (e) { console.error("Error loading user ticket notifs", e); }
      }
      if (employeeId && employeeId !== userId) {
        try {
          const empNotifs = await getTicketNotifications(employeeId);
          if (Array.isArray(empNotifs)) allTickets = [...allTickets, ...empNotifs];
        } catch (e) { console.error("Error loading employee ticket notifs", e); }
      }
      
      // Filter unique by LogID and only unread if needed, though API usually returns unread
      const uniqueTickets = Array.from(new Set(allTickets.map(t => t.LogID)))
        .map(id => allTickets.find(t => t.LogID === id))
        .sort((a, b) => new Date(b.ActionDate || b.CreatedDate) - new Date(a.ActionDate || a.CreatedDate));
        
      setTicketNotifications(uniqueTickets || []);
    } catch (error) {
      console.error("Error loading maintenance notifications:", error);
      setTicketNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleNotificationClick = async (notif) => {
    try {
      await markTicketNotificationAsRead(notif.LogID, notif.NotificationRecipientID);
      loadNotifications();
    } catch (error) {
      console.error("Error marking ticket notification as read:", error);
    }
    setMenu(false);
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={() => setMenu(!menu)}
        className="dropdown d-inline-block"
        tag="li"
      >
        <DropdownToggle
          className="btn header-item noti-icon position-relative"
          tag="button"
          id="page-header-maintenance-notifications-dropdown"
        >
          <i className="bx bx-wrench" />
          {ticketNotifications.length > 0 && (
            <span className="badge bg-danger rounded-pill">
              {ticketNotifications.length}
            </span>
          )}
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0" style={{ width: "350px" }}>
          <div className="p-3 bg-soft-primary border-bottom">
            <Row className="align-items-center">
              <Col>
                <h6 className="m-0 font-size-15 fw-bold text-primary"> <i className="bx bx-wrench me-1"></i> {props.t("Ticket Updates")} </h6>
              </Col>
              <div className="col-auto">
                <Link to="/GmsMaintenanceTicketsReport" className="small text-muted" onClick={() => setMenu(false)}>
                  View Report
                </Link>
              </div>
            </Row>
          </div>

          <SimpleBar style={{ maxHeight: "300px" }}>
            {loading && ticketNotifications.length === 0 ? (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                <p className="mt-2 text-muted small">Checking for updates...</p>
              </div>
            ) : ticketNotifications.length === 0 ? (
              <div className="text-center py-5">
                <div className="avatar-md mx-auto mb-3">
                    <span className="avatar-title bg-light text-primary rounded-circle fs-3">
                        <i className="bx bx-check-double"></i>
                    </span>
                </div>
                <p className="text-muted mb-0">No pending ticket updates</p>
              </div>
            ) : (
              ticketNotifications.map((notif, index) => (
                <Link 
                  to={`/GmsMaintenanceTicketsReport?ticketID=${notif.TicketID || notif.TicketId}&ticketCode=${notif.TicketCode}`} 
                  className="text-reset notification-item d-block" 
                  key={`ticket-notif-${index}`}
                  onClick={() => handleNotificationClick(notif)}
                >
                  <div className="d-flex p-3 border-bottom-dashed hover-bg-light">
                    <div className="avatar-xs me-3">
                      <span className={`avatar-title ${notif.Priority === 0 ? 'bg-danger' : 'bg-info'} rounded-circle font-size-16`}>
                        <i className="bx bx-wrench" />
                      </span>
                    </div>
                    <div className="flex-grow-1 overflow-hidden">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                          <h6 className="mt-0 mb-0 font-size-14 text-primary fw-bold">#{notif.TicketCode}</h6>
                          <small className="text-muted"><i className="mdi mdi-clock-outline me-1"></i>{formatDate(notif.ActionDate || notif.CreatedDate)}</small>
                      </div>
                      <p className="mb-1 text-muted text-truncate font-size-12">
                        {notif.ActionDescription || notif.Comments || notif.TicketDescription || "Status updated"}
                      </p>
                      <div className="d-flex align-items-center gap-2 mt-1">
                         <Badge color="soft-primary" className="text-primary">{notif.NewStatusName || 'Updated'}</Badge>
                         {notif.Priority === 0 && <Badge color="soft-danger" pill>Critical</Badge>}
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </SimpleBar>
          
          <div className="p-2 border-top d-grid">
            <Link 
              className="btn btn-sm btn-link font-size-14 text-center" 
              to="/GmsMaintenanceTicketsReport"
              onClick={() => setMenu(false)}
            >
              <i className="mdi mdi-arrow-right-circle me-1"></i> 
              View All Maintenance Tickets
            </Link>
          </div>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

MaintenanceNotificationDropdown.propTypes = {
  t: PropTypes.any
};

export default withTranslation()(MaintenanceNotificationDropdown);
